/* eslint-disable import/prefer-default-export */
import { useState, useEffect, useMemo } from 'react';
import fetchAllDeploymentsForQB from './fetchAllDeploymentsForQueryBuilder';
import { buildQueryBuilderFieldsConfig } from './buildQueryBuilderFieldsConfig';
import { getFieldRowsFromQueryBuilderFieldsProp } from './queryBuilderFieldRows'; // eslint-disable-line max-len
import { safeStringify } from './queryBuilderUtils';

/**
 * Builds RAQB `fields` map from Crowdflow/Venue `fields` prop plus deployment
 * options for check-in deployment select.
 *
 * Stabilizes `fields` by content (JSON) so parent re-renders with a new object
 * reference do not rebuild config every frame (which caused infinite update
 * loops with Query's useEffect sync).
 */
export function useQueryBuilderRaqbFields(fields, projectId, options = {}) {
    const { excludeCheckinFields = false } = options;
    const [deploymentList, setDeploymentList] = useState([]);
    const [deploymentsLoading, setDeploymentsLoading] = useState(!!projectId);
    const [stableFieldsJson, setStableFieldsJson] = useState(() =>
        safeStringify(fields)
    );

    useEffect(() => {
        const next = safeStringify(fields);
        setStableFieldsJson((prev) => (prev === next ? prev : next));
    }, [fields]);

    useEffect(() => {
        if (!projectId) {
            setDeploymentList([]);
            setDeploymentsLoading(false);
            return undefined;
        }
        let cancelled = false;
        setDeploymentsLoading(true);
        fetchAllDeploymentsForQB(projectId)
            .then((list) => {
                if (!cancelled) {
                    setDeploymentList(Array.isArray(list) ? list : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDeploymentList([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setDeploymentsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [projectId]);

    const fieldConfig = useMemo(() => {
        let parsed = null;
        try {
            parsed = stableFieldsJson ? JSON.parse(stableFieldsJson) : null;
        } catch {
            parsed = null;
        }
        return buildQueryBuilderFieldsConfig(
            getFieldRowsFromQueryBuilderFieldsProp(parsed, {
                excludeCheckinFields,
            }),
            { deploymentList }
        );
    }, [stableFieldsJson, deploymentList, excludeCheckinFields]);

    return { fieldConfig, loading: deploymentsLoading };
}
